import { Component, Element, Prop, VNode, Watch, h } from '@stencil/core';

import * as Pipeline from '../core/utils/pipeline';
import { AdsFooterLegalLink } from './components/ads-footer-legal-link/ads-footer-legal-link';
import { AdsFooterLinkSection } from './components/ads-footer-link-section/ads-footer-link-section';
import { AdsFooterSocialLink } from './components/ads-footer-social-link/ads-footer-social-link';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Footer, FooterLegalLink, FooterSection, FooterSocialLink } from './ads-footer.types';
import { Theme } from '../core/types/globalTypes';
import { generateId } from '../core/utils/components';

/**
 * The Footer component is a content container at the bottom of a page that displays links to social media and to content across the Arm digital estate.
 * By having the footer on each page, the user has a consistent set of navigation actions.
 * @tag `<ads-footer>`
 * @slot copyright-section - The copyright string
 * @slot logo - The logo area
 * @example
 * <ads-footer id="example">
 *   <div slot="copyright-section">
 *     Copyright © 2021 Arm Limited (or its affiliates). All rights reserved.
 *   </div>
 * </ads-footer>
 */
@Component({
  tag: 'ads-footer',
  styleUrl: 'ads-footer.scss',
  shadow: true,
})
export class AdsFooter implements BaseComponent {
  /**
   * @hidden
   */
  component: string;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-footer'));
    this.component = 'ads-footer';
  }

  @Element() private el: HTMLAdsFooterElement;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * Text to display as the aria-label of the component (optional)
   */
  @Prop({ reflect: true }) srLabel?: string;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * The footer data
   */
  @Prop() data: Footer;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-footer'));
  }

  protected componentDidLoad(): void {
    Pipeline.addComponent(this.el);
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
  }

  protected render(): VNode {
    const copyrightEl = Array.from(this.el.querySelectorAll('[slot="copyright-section"]'));
    const copyright =
      copyrightEl?.length > 0
        ? ((
            <div class="c-footer__row">
              <div class="c-footer__column">
                <slot name="copyright-section"></slot>
              </div>
            </div>
          ) as HTMLElement)
        : null;

    const slottedLogo = ['c-footer__slotted'];
    this.data?.socialLinks?.length > 0 ? slottedLogo.push('c-footer__column-6') : slottedLogo.push('c-footer__column');

    const footerAttributes = {
      'aria-label': this.srLabel,
    };

    return (
      <footer class="c-footer" {...footerAttributes}>
        <div class="c-footer__container">
          <div class="c-footer__row">
            {this.data?.sections?.map(
              (section: FooterSection) =>
                (<AdsFooterLinkSection sectionHeader={section.sectionHeader} sectionLinks={section.sectionLinks} />) as VNode
            )}
          </div>
        </div>
        <div class="c-footer__container">
          <div class="c-footer__row">
            <div class="c-footer__column">
              <hr />
            </div>
          </div>
          <div class="c-footer__row">
            {this.data?.socialLinks?.length > 0 ? (
              <div class="c-footer__column-6">
                <ul class="c-footer__social-link-list">
                  {this.data?.socialLinks?.map(
                    (socialLink: FooterSocialLink) =>
                      (<AdsFooterSocialLink icon={socialLink.icon} title={socialLink.title} link={socialLink.link} />) as VNode
                  )}
                </ul>
              </div>
            ) : null}
            <div class={slottedLogo.join(' ')}>
              <slot name="logo" />
            </div>
          </div>
          {this.data?.legalLinks?.length > 0 ? (
            <div class="c-footer__row">
              <div class="c-footer__column">
                <ul class="c-footer__legal-link-list">
                  {this.data?.legalLinks?.map(
                    (legalLink: FooterLegalLink) =>
                      (<AdsFooterLegalLink title={legalLink.title} link={legalLink.link} text={legalLink.text} />) as VNode
                  )}
                </ul>
              </div>
            </div>
          ) : null}
          {copyright}
        </div>
      </footer>
    ) as VNode;
  }
}
